import type {
  Auth0ContextInterface,
  GetTokenSilentlyOptions,
  User,
} from '@auth0/auth0-react';
import { AuthenticationError, GenericError } from '@auth0/auth0-react';

import { AccessDeniedError } from './errors';
import { sanitizeRedirectUrl } from './sanitizeRedirectUrl';

/**
 * Calls for access token and handles errors as of https://auth0.com/docs/authenticate/login/configure-silent-authentication#error-responses
 */
async function getAccessTokenSilently(
  auth: Auth0ContextInterface<User>,
  options?: GetTokenSilentlyOptions
) {
  try {
    const token = await auth.getAccessTokenSilently(options);

    if (!token) {
      throw new Error('No access token returned');
    }

    return token;
  } catch (error) {
    if (error instanceof AuthenticationError || error instanceof GenericError) {
      // All of the following errors require a interaction with a user interface
      // by the end-user to resolve the error.
      const redirectToLoginErrors = [
        'login_required',
        'consent_required',
        'interaction_required',
        'account_selection_required',
      ];

      if (
        redirectToLoginErrors.includes(error.error) ||
        redirectToLoginErrors.includes(error.error_description)
      ) {
        await auth.loginWithRedirect({
          authorizationParams: {
            redirect_uri: window.location.origin,
          },
          appState: {
            returnTo: sanitizeRedirectUrl(window.location.href),
          },
        });
        throw new Error(
          `Failed to get access token silently: "${error.error}". Redirecting to login.`,
          { cause: error }
        );
      }

      if (
        error.error === 'access_denied' ||
        error.error_description === 'access_denied'
      ) {
        throw new AccessDeniedError();
      }
    }

    if (error instanceof Error === false) {
      throw new Error('Failed to get access token silently', {
        cause: error,
      });
    }

    throw new Error(`Failed to get access token silently: "${error.message}"`, {
      cause: error,
    });
  }
}

export const authHelpers = {
  getAccessTokenSilently,
};
